<template>
  <div
    id="deleteContextMenu"
    :style="{
      display: deleteShow ? 'initial' : 'none',
    }"
  >
    <button id="delete-button" @click="deletePoint">Delete</button>
  </div>
  <a-row
    class="h-100 d-flex justify-content-around align-items-center"
    :gutter="[24, 24]"
  >
    <a-col span="14" class="h-100 d-flex flex-column">
      <a-card
        hoverable
        :body-style="{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
        }"
        size="small"
        class="annotation-image-card d-flex flex-column flex-grow-1"
      >
        <a-spin
          v-if="imageLoading"
          :indicator="indicator"
          class="m-auto"
          size="large"
        />

        <a-result
          v-else-if="!imageLoading && !depthFrame"
          id="not-found-plane-image"
          class="m-auto"
        >
          <template #title>
            <h6>
              Please press <b>Generate Depth Frame</b> button to create plane
              regions.
            </h6>
          </template>
        </a-result>

        <template #title>
          <div
            class="d-flex"
            style="justify-content: space-between; margin-top: 5px"
          >
            <div class="d-flex">
              <a-radio-group
                v-model:value="selectedOperation"
                size="medium"
                :disabled="imageLoading"
              >
                <a-radio-button value="generate" style="width: max-content">
                  Generate Depth Frame
                </a-radio-button>
                <a-radio-button value="reset" style="width: max-content">
                  Reset Depth Frame
                </a-radio-button>
              </a-radio-group>
            </div>
            <div class="d-flex">
              <span
                class="color-selector ml-auto"
                :style="`background:${textColor}`"
                :disabled="imageLoading"
                :class="{ greyBorder: textColor === 'white' }"
                @click="changeLabelColor"
              />
              <span
                class="color-selector"
                :style="`background:${
                  textColor === 'black' ? 'white' : 'black'
                }`"
                :disabled="imageLoading"
                :class="{ greyBorder: textColor === 'black' }"
                @click="changeLabelColor"
              />
            </div>

            <div>
              <p style="color: red; margin-right: 10vw; font-size: small">
                You cannot place points inside grey shaded area.
              </p>
            </div>
          </div>
        </template>

        <template v-if="!imageLoading && image" #cover>
          <v-stage
            id="plane-define-stage"
            :key="groups"
            ref="stage"
            :config="stageSize"
            @click="handleStageClick"
            @mousedown="handleStageMouseDown"
            @touchstart="handleStageMouseDown"
            @contextmenu="disableWebMenu"
          >
            <v-layer
              id="region-define-canvas-layer"
              ref="layer"
              @mouseup="handleLayerMouseUp"
            >
              <v-image
                id="region-define-canvas-image"
                ref="image"
                :config="imageConfig"
              />
              <v-group
                v-for="(circle, index1) in groups.circles"
                :key="index1"
                @contextmenu="handleDeleteMenuShow"
              >
                <v-circle
                  v-for="(point, index2) in circle"
                  :key="index2"
                  :config="point"
                  :draggable="!Object.keys(selectedObject).length"
                  :dragBoundFunc="(pos) => dragBoundFunc(pos, index1, index2)"
                  @dragend="handleTransformCircle"
                />
              </v-group>
              <v-transformer id="region-define-transformer" ref="transformer" />
            </v-layer>
          </v-stage>
        </template>
      </a-card>
    </a-col>

    <a-col span="8" class="h-100 d-flex flex-column">
      <a-list
        id="marked-region-obj-list"
        bordered
        class="static-objects-list"
        :loading="{ spinning: !isObjectFetched, size: 'large' }"
        item-layout="horizontal"
        :data-source="planeObjectsList"
        size="small"
      >
        <template #header>
          <span>Defined Planes</span>
        </template>
        <template #renderItem="{ item, index }">
          <a-list-item :id="'region-obj-' + index" style="cursor: pointer">
            <a-list-item-meta>
              <template #title>
                <a-row>
                  <a-col span="8">
                    <template
                      v-if="item.equation && item.equation?.length > 3"
                      style="margin-left: 10px"
                    >
                      <CheckOutlined
                        style="color: green; padding: 10px; fontsize: 18px"
                      />
                    </template>

                    <template
                      v-if="
                        item?.equation === null || item.equation?.length < 3
                      "
                      style="margin-left: 10px"
                    >
                      <ExclamationOutlined style="color: red; padding: 10px" />
                    </template>

                    <span
                      :id="'plane-name-' + item.name"
                      :class="{ 'disable-click': existedObject[item.name] }"
                    >
                      {{ item.name }}
                    </span>
                  </a-col>

                  <a-col span="16">
                    <div
                      class="d-flex align-items-center justify-content-end"
                      style="gap: 16px"
                    >
                      <input
                        v-model="item.points.plane_color"
                        type="color"
                        :disabled="!imageLoading && !depthFrame"
                        @change="handleChangePlaneColor(item)"
                        @keyup="(e) => handlePressEnter(e, item)"
                      />

                      <h6 class="mb-0">
                        {{ groups?.circles[item.name]?.length }}
                      </h6>

                      <a-space class="d-flex">
                        <a-tooltip>
                          <template #title> Add Point </template>
                          <a-button
                            shape="circle"
                            :style="{
                              backgroundColor:
                                addPointOnPlane[item.id] === true
                                  ? 'blue'
                                  : 'white',
                              color:
                                addPointOnPlane[item.id] === true
                                  ? 'white'
                                  : 'blue',
                            }"
                            :disabled="!imageLoading && !depthFrame"
                            @click="setNewObjectConfigToDraw(item)"
                          >
                            <template #icon>
                              <PlusOutlined />
                            </template>
                          </a-button>
                        </a-tooltip>

                        <!-- <a-tooltip>
                          <template #title>Remove Point</template>
                          <a-button
                            shape="circle"
                            @click="deleteLastPoint(item.name)"
                          >
                            <template #icon>
                              <MinusOutlined />
                            </template>
                          </a-button>
                        </a-tooltip> -->

                        <a-tooltip>
                          <template #title>Calculate Plane Equation</template>
                          <a-button
                            shape="circle"
                            :disabled="!imageLoading && !depthFrame"
                            @click="calculateEquation(item)"
                          >
                            <template #icon>
                              <CalculatorOutlined
                                v-if="
                                  calculatePlanceEquLoader[item.id] == null ||
                                  !calculatePlanceEquLoader[item.id]
                                "
                              />
                              <a-spin
                                size="small"
                                style="
                                  display: flex;
                                  align-items: center;
                                  justify-content: center;
                                "
                                v-else
                              />
                            </template>
                          </a-button>
                        </a-tooltip>

                        <a-tooltip>
                          <template #title>Reset Plane Equation</template>
                          <a-button
                            shape="circle"
                            :disabled="!imageLoading && !depthFrame"
                            @click="resetPlaneEquation(item)"
                          >
                            <template #icon>
                              <RedoOutlined />
                            </template>
                          </a-button>
                        </a-tooltip>

                        <a-switch
                          v-model:checked="overlayCheck[item.id]"
                          checked-children="overlay"
                          un-checked-children="depth"
                          :disabled="
                            (!imageLoading && !depthFrame) ||
                            !item.equation ||
                            item.equation.length < 3
                          "
                          :loading="overlayCheck[item.id] && overlayLoading"
                          @change="handleOverlayChange(item)"
                        />
                      </a-space>
                    </div>
                  </a-col>
                </a-row>
              </template>
            </a-list-item-meta>
          </a-list-item>
        </template>
      </a-list>
    </a-col>
  </a-row>
</template>
<script>
import objectsMarkingMixin from '@/mixins/objectsMarking';
import TaskObjectService from '@/services/taskObjectsMarking';
import { colorsConfig } from '@/utils/detector';
import {
  BorderOutlined,
  CalculatorOutlined,
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  ExclamationOutlined,
  FontSizeOutlined,
  MinusOutlined,
  PlusOutlined,
  RedoOutlined,
  RiseOutlined,
  SettingOutlined,
} from '@ant-design/icons-vue';
import DepthRegionService from 'src/services/3dPlaneEquation';
import S3Service from 'src/services/s3';
import { mapActions, mapGetters } from 'vuex';
import { parseStringCoordsToObj } from '../../LabelData/ObjectAnnotation/helpers';
import * as OBJECT from '../../LabelData/ObjectAnnotation/object';

export default {
  components: {
    DeleteOutlined,
    ExclamationCircleOutlined,
    EditOutlined,
    FontSizeOutlined,
    BorderOutlined,
    PlusOutlined,
    MinusOutlined,
    RiseOutlined,
    ExclamationOutlined,
    CheckOutlined,
    CalculatorOutlined,
    RedoOutlined,
    SettingOutlined,
  },

  mixins: [objectsMarkingMixin],

  inject: ['toast'],
  setup: () => ({
    OBJECT,
  }),

  data() {
    return {
      overlayImages: {},
      overlayCheck: {},
      depthFrame: null,
      depthFrameUri: [],
      imageLoading: true,
      image: null,
      objectList: [],
      overlayLoading: false,
      selectedCircleId: '',
      textColor: 'white',
      selectedCircleKey: '',
      selectedOperation: '',
      calculatePlanceEquLoader: {},
      annotationSaveLoading: false,
      imageDimensions: { width: 0, height: 0 },
      stageSize: { width: 640, height: 480 },
      isObjectFetched: false,
      zeroDepthPixel: {},
      zeroDepthXAxis: 0,
      prevPoints: { x: 320, y: 240 },
      prevPointType: null,
      groups: {
        circles: {},
        labels: {},
      },
      existedObject: {},
      deleteShow: false,
      addPointOnPlane: {},
      selectedObject: {},
      overlayChanged: false,
    };
  },

  computed: {
    ...mapGetters([
      'selectedTask',
      'taskPlaneObjectsList',
      'taskObjects',
      'planeObjectsList',
      'organization',
    ]),

    imageConfig() {
      return {
        image: this.image,
        name: 'currentImage',
        ...this.stageSize,
      };
    },
  },

  watch: {
    groups() {
      this.updateExistedObjects();
    },
    selectedOperation(val) {
      if (val == 'generate') {
        this.generateDepthFrame();
      } else if (val == 'reset') {
        this.resetDepthFrame();
      }
    },
    async textColor(newColor) {
      this.imageLoading = true;

      if (newColor == 'black') {
        this.depthFrame = this.depthFrameUri[1];
      } else {
        this.depthFrame = this.depthFrameUri[0];
      }
      await this.setImage('depth', null);
      this.imageLoading = false;
    },
  },

  async mounted() {
    try {
      this.subscribeEvents();
      this.isObjectFetched = false;
      await this.fetchDepthImage();
    } catch (err) {
      console.error(err);
    } finally {
      this.isObjectFetched = true;
      this.imageLoading = false;
    }
  },

  unmounted() {
    this.removeEvents();
    this.zeroDepthPixel = {};
    this.groups = {
      rectangles: {},
      labels: {},
    };
  },

  methods: {
    ...mapActions([
      'getTaskPlaneObjectsList',
      'setTaskImageMarkingUrl',
      'addPlaneObject',
    ]),

    async fetchDepthImage() {
      try {
        await this.setZeroDepthPixelAndFrameUri();
        await this.setImage('depth', null);
        await this.updateImageConfig();
        await this.renderExistingStaticObjectList();
        this.planeObjectsList.forEach((item) => {
          this.overlayCheck[item.id] = false;
        });
      } catch (err) {
        console.error(err);
      }
    },

    async handleOverlayChange(item) {
      this.overlayChanged = true;
      await this.setImage(
        this.overlayCheck[item.id] == true ? 'overlay' : 'depth',
        item.id
      );
      this.overlayChanged = false;
    },

    removeEvents() {
      window.removeEventListener('resize', this.ResizeStageObjects);
    },

    subscribeEvents() {
      window.addEventListener('resize', this.ResizeStageObjects);
    },

    ResizeStageObjects(e) {
      this.$nextTick(() => {
        const container = document.getElementsByClassName('konvajs-content');
        if (!container?.length) return;
        const cardCover = container[0];
        const { circles } = this.groups;
        this.normalizeCircles(circles);
        this.updateStageAndImageSize(cardCover);
        this.deNormalizeCircle(circles);
      });
    },

    normalizeCircles(circles) {
      for (const [key1, plane] of Object.entries(circles)) {
        for (const [key2, item] of Object.entries(plane)) {
          item.x = Math.round(
            (item.x * this.zeroDepthXAxis) / this.stageSize.width
          );
          item.y = Math.round((item.y * 480) / this.stageSize.height);
        }
      }
    },

    deNormalizeCircle(circles) {
      for (const [key1, plane] of Object.entries(circles)) {
        for (const [key2, item] of Object.entries(plane)) {
          item.x = (item.x * this.stageSize.width) / this.zeroDepthXAxis;
          item.y = (item.y * this.stageSize.height) / 480;
        }
      }
    },

    disableWebMenu(e) {
      e.evt?.preventDefault();
      return false;
    },

    changeLabelColor() {
      if (this.textColor === 'white') this.textColor = 'black';
      else this.textColor = 'white';
    },

    checkPointInsideGreyRegion(width, height, pointx, pointy) {
      let x = Math.round((pointx / width) * this.zeroDepthXAxis);
      let y = Math.round((pointy / height) * 480);
      let isZeroRegion = false;

      if (this.zeroDepthPixel.hasOwnProperty(x)) {
        let val = this.zeroDepthPixel[x];
        if (val.hasOwnProperty(y)) {
          isZeroRegion = true;
        }
      }

      if (pointx > width || pointy > height || pointx < 0 || pointy < 0) {
        isZeroRegion = true;
      }
      return isZeroRegion;
    },
    checkIfpointOverlapEachOther(width, height, pointx, pointy) {
      let checkPointsOverlay = false;
      let x = Math.round((pointx / width) * this.zeroDepthXAxis);
      let y = Math.round((pointy / height) * 480);

      for (const [key1, plane] of Object.entries(this.groups.circles)) {
        for (const [key2, point] of Object.entries(plane)) {
          let ptx = Math.round((point.x / width) * this.zeroDepthXAxis);
          let pty = Math.round((point.y / height) * 480);
          if (Math.abs(x - ptx) < 10 && Math.abs(y - pty) < 10) {
            checkPointsOverlay = true;
            return checkPointsOverlay;
          }
        }
      }
      return checkPointsOverlay;
    },
    dragBoundFunc(pos, plainName, circleIndex) {
      const circle = this.groups.circles[plainName].find(
        (obj) => obj.name === `${plainName}-${circleIndex}`
      );
      const container = this.$refs.stage;
      let pointx = pos.x;
      let pointy = pos.y;
      let valx = 0;
      let valy = 0;
      const inGrey = this.checkPointInsideGreyRegion(
        container.oldProps.width,
        container.oldProps.height,
        pos.x,
        pos.y
      );

      const overlapRegion = this.checkIfpointOverlapEachOther(
        container.oldProps.width,
        container.oldProps.height,
        pos.x,
        pos.y
      );

      if (inGrey || overlapRegion) {
        valx = circle.x;
        valy = circle.y;
      } else {
        const minX = 10;
        const minY = 10;
        const maxX = Math.abs(container.oldProps.width - 10);
        const maxY = Math.abs(container.oldProps.height - 10);

        valx = Math.min(Math.max(minX, pointx), maxX);
        valy = Math.min(Math.max(minY, pointy), maxY);
      }
      return {
        x: valx,
        y: valy,
      };
    },

    async getZeroDepthFrame() {
      try {
        var [error, data] = await DepthRegionService.getZeroDepthVideoFrames(
          this.selectedTask
        );
        if (error) {
          return null;
        }
      } catch (error) {
        return null;
      }
      return data;
    },

    handleZeroDepthFrameError(error) {
      this.imageLoading = false;
      let errMessage =
        typeof error.response !== 'undefined'
          ? error.response.data.message
          : error.message;
      const msg =
        errMessage === 'undefined' || errMessage.length == 0
          ? 'Depth frame not found'
          : errMessage;
      if (typeof msg === 'string') this.toast.error(msg);
    },

    async convertDepthFrameToUrl(frameType, planeId) {
      let imageUrl = '';
      const displaySpinner = this.overlayChanged;
      if (frameType == 'depth') {
        const [error2, url] = await S3Service.generatePresignedUrl(
          `${this.organization}-training`,
          this.depthFrame,
          '',
          displaySpinner
        );
        if (error2) {
          console.error('Error:', error2);
          return;
        }
        imageUrl = url;
      } else {
        if (
          planeId == null ||
          this.overlayImages.hasOwnProperty(planeId) == false ||
          this.overlayImages[planeId] == null
        ) {
          const [error, data] = await DepthRegionService.getOverlayImage(
            this.selectedTask,
            planeId,
            displaySpinner
          );
          if (error) {
            this.toast.error(
              'Overlay image not found.Please calculate the plane equation first'
            );
            this.overlayCheck[planeId] = false;
            return;
          }
          this.overlayImages[planeId] = data.visualize_depth_path;
        }
        const [error3, url] = await S3Service.generatePresignedUrl(
          `${this.organization}-training`,
          this.overlayImages[planeId],
          '',
          displaySpinner
        );
        if (error3) {
          console.error('Error:', error3);
          return;
        }
        imageUrl = url;
      }
      return imageUrl;
    },
    async resetDepthFrame() {
      let payload = {};
      this.planeObjectsList.forEach((item) => {
        payload[item.id] = [];
      });
      const [error, data] = await DepthRegionService.resetDepthVideoFrames(
        this.selectedTask,
        { points: payload }
      );
      this.selectedOperation = '';

      if (error) {
        this.toast.error('Unable to reset depth frame');
        return;
      }
      await this.renderExistingStaticObjectList();
      this.planeObjectsList.forEach((item) => {
        this.overlayCheck[item.id] = false;
      });
      this.toast.success('Depth frame reset successfully');
    },
    async generateDepthFrame() {
      this.imageLoading = true;
      this.isObjectFetched = false;
      const [error, data] = await DepthRegionService.generateDepthVideoFrames(
        this.selectedTask,
        {}
      );
      this.selectedOperation = '';

      if (error) {
        this.toast.error('Unable to generate depth frame');
        this.imageLoading = false;
        this.isObjectFetched = true;
        return;
      }
      this.textColor = 'white';
      await this.fetchDepthImage();

      this.isObjectFetched = true;
      this.imageLoading = false;
      this.toast.success('Successfully generated depth frame');
    },

    async setZeroDepthPixelAndFrameUri() {
      const response = await this.getZeroDepthFrame();
      if (response === null) return;
      this.zeroDepthPixel = response?.zero_depth_pixel
        ? response.zero_depth_pixel
        : {};
      this.zeroDepthXAxis = Object.keys(this.zeroDepthPixel).length;
      this.depthFrameUri = [
        response?.zero_depth_pixel_white_image_path,
        response?.zero_depth_pixel_black_image_path,
      ];
      this.depthFrame = this.depthFrameUri.length ? this.depthFrameUri[0] : '';
    },

    async calculateEquation(selectedPlane) {
      if (this.imageLoading || !this.depthFrame) {
        this.toast.error('Image not loaded properly');
        return;
      }
      this.calculatePlanceEquLoader[selectedPlane.id] = true;
      let payload = {
        task: selectedPlane.task,
      };
      const [error, data] = await DepthRegionService.calculatePlaneEquation(
        selectedPlane.id,
        payload
      );
      if (error) {
        this.toast.error(this.parseCalculatePlaneErrorMessage(error));
        this.calculatePlanceEquLoader[selectedPlane.id] = false;
        return;
      }

      if (data.data.depth_video.path_to_overlay_image != null) {
        this.overlayImages[selectedPlane.id] =
          data.data.depth_video.path_to_overlay_image +
          '-' +
          selectedPlane.id +
          '.jpg';
      }
      this.toast.success('Plane Equation Calculated Successfully!');
      await this.renderExistingStaticObjectList();
      this.calculatePlanceEquLoader[selectedPlane.id] = false;
    },

    parseCalculatePlaneErrorMessage(error) {
      const errorMessage = 'Failed to calculate plane equation.';
      try {
        return typeof error.response !== 'undefined'
          ? Object.prototype.hasOwnProperty.call(
              error.response.data,
              'non_field_errors'
            )
            ? error.response.data['non_field_errors'][0]
            : Object.prototype.hasOwnProperty.call(
                error.response.data,
                'message'
              )
            ? error.response.data.message
            : errorMessage
          : error.message;
      } catch (err) {
        return errorMessage;
      }
    },

    updateExistedObjects() {
      const { labels } = this.groups;
      this.existedObject = Object.values(labels).reduce((res, el) => {
        res[el.text] = true;
        return res;
      }, {});
    },

    async updateImageConfig() {
      try {
        const canvas = await this.getCanvasElement();
        const { width, height } = canvas.getBoundingClientRect();
        this.imageDimensions = {
          width,
          height,
        };
        this.stageSize = {
          width,
          height,
        };
      } catch (err) {
        console.error(err);
      }
    },

    getCanvasElement() {
      return new Promise((resolve, reject) => {
        const maxWaitTime = 8000; // 8 seconds
        const intervalTime = 1000; // 1 seconds
        let elapsedTime = 0;

        const interval = setInterval(() => {
          const canvas = document.getElementsByTagName('canvas')[0];

          if (canvas) {
            clearInterval(interval);
            clearTimeout(timeout);
            resolve(canvas);
          }

          elapsedTime += intervalTime;
        }, intervalTime);

        const timeout = setTimeout(() => {
          clearInterval(interval);
          reject(new Error('Canvas element not found within 10 seconds.'));
        }, maxWaitTime);
      });
    },

    isObjectExist(name) {
      const { labels } = this.groups;
      const isExist = Object.values(labels).some((o) => o.text === name);
      return isExist;
    },

    // set image
    async setImage(imageType, planeId) {
      try {
        if (planeId != null || planeId != undefined) {
          for (const [key, value] of Object.entries(this.overlayCheck)) {
            if (key != planeId) {
              this.overlayCheck[key] = false;
            }
          }
        }

        if (!this.depthFrame) {
          return;
        }
        let url = null;
        try {
          this.overlayLoading = true;
          url = await this.convertDepthFrameToUrl(imageType, planeId);
        } catch (e) {
          this.overlayLoading = false;
          return;
        }
        const img = new window.Image();
        const imgUrl = url?.presigned_url;
        if (!imgUrl) {
          this.imageLoading = false;
          return;
        }
        img.src = imgUrl;
        img.onload = () => {
          this.image = img;
          this.image.width = img.naturalWidth;
          this.image.height = img.naturalHeight;
          this.imageLoading = false;
          this.overlayLoading = false;
        };
        img.onerror = () => {
          this.imageLoading = false;
          this.overlayLoading = false;
        };
      } catch (err) {
        console.error(err);
      }
    },
    getRandomInt(min, max) {
      min = Math.ceil(min);
      max = Math.floor(max);
      return Math.floor(Math.random() * (max - min)) + min; //The maximum is exclusive and the minimum is inclusive
    },
    // add new object in groups
    async setNewObjectConfigToDraw(planeObj) {
      if (this.imageLoading || !this.depthFrame) {
        this.toast.error('Image not loaded properly');
        return;
      }
      let plainPoints = planeObj.points;
      if (!plainPoints['plane_points']) return;
      this.updateAddPointOnPlane(planeObj);
      if (this.addPointOnPlane[planeObj.id]) this.selectedObject = planeObj;
      else this.selectedObject = {};
    },

    updateAddPointOnPlane(planeObj) {
      const keys = Object.keys(this.addPointOnPlane);
      keys.forEach((key) => {
        if (key != planeObj.id) this.addPointOnPlane[key] = false;
      });

      if (
        this.addPointOnPlane[planeObj.id] === null ||
        this.addPointOnPlane[planeObj.id] === undefined
      )
        this.addPointOnPlane[planeObj.id] = true;
      else
        this.addPointOnPlane[planeObj.id] = !this.addPointOnPlane[planeObj.id];
    },

    async addPointsInGroup(pos) {
      if (!Object.keys(this.selectedObject).length) return;
      const planeObj = this.selectedObject;
      let plainPoints = planeObj.points;
      let newCircleName =
        planeObj.name + '-' + plainPoints['plane_points'].length;

      let pointx = Math.round(
        (pos.x * this.zeroDepthXAxis) / this.stageSize.width
      );
      let pointy = Math.round((pos.y * 480) / this.stageSize.height);
      plainPoints['plane_points'].push([pointx, pointy]);
      planeObj.points = JSON.stringify(plainPoints);
      await this.addPlaneObject(planeObj);
      planeObj.points = plainPoints;
      this.groups = this.addCircleFromObjects(
        this.groups,
        [pos.x, pos.y],
        newCircleName,
        planeObj
      );
    },

    handleDeleteMenuShow(e) {
      if (Object.keys(this.selectedObject).length) return;
      e.evt?.preventDefault();
      if (!e.target.hasOwnProperty('parent')) {
        return;
      }
      const pos = this.$refs.stage.getStage().getPointerPosition();
      const deleteContextMenu = document.getElementById('deleteContextMenu');
      const name = e.target.name();
      const stage = document.getElementById('plane-define-stage');
      if (name && name !== 'currentImage') {
        const { top, left } = stage.getBoundingClientRect();
        const { width } = deleteContextMenu.getBoundingClientRect();
        deleteContextMenu.style.top = top + pos.y + 4 + 'px';
        deleteContextMenu.style.left = `${left + pos.x - width}px`;
        this.deleteShow = true;

        const circle = this.groups.circles[name];
        if (circle) {
          this.selectedCircleKey = name;
          this.selectedCircleId = name;
        }
      }
    },

    handleStageClick(e) {
      e.evt?.preventDefault();
      if (e.evt?.button === 2) return;
      const deleteContextMenu = document.getElementById('deleteContextMenu');
      if (deleteContextMenu.style.display === 'initial') {
        this.selectedCircleId = null;
        this.deleteShow = false;
      }
    },

    // set Transformer when click on stage
    async handleStageMouseDown(e) {
      if (!e.target.hasOwnProperty('parent')) {
        return;
      }
      const clickedOnTransformer =
        e.target.getParent().className === 'Transformer';
      if (clickedOnTransformer) {
        return;
      }
      const pointName = e.target.name();
      const planeName = pointName.split('-')[0];
      const circles = this.groups.circles[planeName];

      if (circles != null || circles != undefined) {
        this.selectedCircleKey = planeName;
        this.selectedCircleId = pointName;
      } else {
        this.selectedCircleKey = '';
        this.selectedCircleId = '';
      }

      // if (!e.target.hasOwnProperty('parent')) return;

      // clicked on stage - clear selection
      if (!e.target?.getStage) return;
      if (e.target === e.target.getStage()) {
        return;
      }
    },

    async updateCirclePoints(pointname) {
      const planeName = pointname.split('-')[0];
      const circles = this.groups.circles[planeName];
      let planeData;
      this.planeObjectsList.forEach((plane) => {
        if (plane.name == planeName) {
          planeData = plane;
        }
      });

      let updatedCircle;
      circles?.forEach((circle) => {
        if (circle.name == pointname) {
          updatedCircle = circle;
        }
      });

      var plainPoints = planeData.points;
      let pointx = Math.round(
        (updatedCircle.x * this.zeroDepthXAxis) / this.stageSize.width
      );
      let pointy = Math.round((updatedCircle.y * 480) / this.stageSize.height);

      plainPoints.plane_points.splice(pointname.split('-')[1], 1, [
        pointx,
        pointy,
      ]);

      planeData.points = JSON.stringify(plainPoints);
      await this.addPlaneObject(planeData);
      planeData.points = plainPoints;
    },

    // display existing objects
    renderExistingStaticObjectList() {
      return new Promise(async (resolve, reject) => {
        await this.getTaskPlaneObjectsList();
        const existGroup = {
          circles: {},
          labels: {},
        };
        this.planeObjectsList.forEach((obj, index) => {
          obj.points = JSON.parse(obj.points);
          obj.points.plane_color =
            obj.points?.plane_color || colorsConfig[index];
          const { plane_points, label } = this.createCircleLabelGroup(
            obj,
            this.zeroDepthXAxis
          );
          existGroup.circles[obj.name] = plane_points;
        });
        this.groups = existGroup;
        resolve();
      });
    },

    async handleChangePlaneColor(planeObj) {
      if (
        Object.prototype.hasOwnProperty.call(this.groups.circles, planeObj.name)
      )
        this.groups.circles[planeObj.name].forEach((obj) => {
          obj.fill = planeObj.points.plane_color;
        });

      let plainPoints = planeObj.points;
      planeObj.points = JSON.stringify(plainPoints);
      await this.addPlaneObject(planeObj);
      planeObj.points = plainPoints;
    },

    getNewlyAddedObjs() {
      return this.taskObjects.filter((t) => {
        let obj_exists = false;
        this.taskPlaneObjectsList.filter((o) => {
          if (o.name === t.name) {
            obj_exists = true;
          }
        });
        return !obj_exists && t.is_3d;
      });
    },

    getObjectListFromState() {
      const new_regions = this.getNewlyAddedObjs().map((o) => {
        return {
          ...o,
          cordinates_of_static_object: parseStringCoordsToObj(
            o.cordinates_of_static_object
          ),
          actual_name: o.name,
        };
      });

      let k = this.taskPlaneObjectsList
        .filter((t) => t.step_id === null)
        .map((o) => {
          return {
            ...o,
            cordinates_of_static_object: parseStringCoordsToObj(
              o.cordinates_of_static_object
            ),
            actual_name: o.name,
          };
        });

      k = k.concat(new_regions);
      return k;
    },

    async resetPlaneEquation(planeObj) {
      this.overlayCheck[planeObj.id] = false;
      await this.setImage('depth', planeObj.id);
      planeObj.equation = null;
      let plainPoints = planeObj.points;
      planeObj.points = JSON.stringify(plainPoints);
      await this.addPlaneObject(planeObj);
      planeObj.points = plainPoints;
    },

    // save objects Marking
    async saveAnnotation() {
      this.annotationSaveLoading = true;
      let annotations = this.getAnnotations();
      let staticObjects = annotations.map(OBJECT.nestCoordinates);
      let updatedAnnotations = this.getUpdatedObjects(staticObjects);
      if (updatedAnnotations.length > 0) {
        await this.updateObjects(updatedAnnotations);
      }

      this.setStateOnSave();
      this.toast.success('Regions Saved Successfully!');
    },

    // getUpdatedObjects(staticObjects) {
    //   const updatedObjects = staticObjects
    //     .filter((o) => this.updatedStaticObjectIds?.has(o.id))
    //     ?.map((obj) => OBJECT.mapStaticObjects(obj, this.selectedTask, false));
    //   const deletedObjects = this.getDeletedObjects()?.map((ob) => ({
    //     ...ob,
    //     cordinates_of_static_object: null,
    //   }));
    //   return [...updatedObjects, ...deletedObjects];
    // },

    updateObjects(data) {
      return new Promise(async (resolve) => {
        const payload = JSON.stringify(data);
        const [error] = await TaskObjectService.updateRegions(payload);
        if (error) {
          this.toast.error('Error occurred in updating objects');
        }
        resolve();
      });
    },

    async setStateOnSave() {
      this.annotationSaveLoading = false;
      await this.getTaskPlaneObjectsList();
    },

    async deleteLastPoint(planeName) {
      var planeData = this.planeObjectsList.find(
        (plane) => plane.name == planeName
      );
      var plainPoints = planeData.points;

      plainPoints.plane_points.splice(plainPoints.plane_points.length - 1, 1);
      this.groups.circles[planeName].splice(
        this.groups.circles[planeName].length - 1,
        1
      );
      planeData.points = JSON.stringify(plainPoints);
      await this.addPlaneObject(planeData);
      planeData.points = plainPoints;
    },

    // remove Object
    async deletePoint() {
      if (
        this.selectedCircleId == '' ||
        this.selectedCircleId == undefined ||
        this.selectedCircleId === null
      )
        return;

      var indexToDelete = this.selectedCircleId.split('-')[1];
      if (indexToDelete < 0) return;

      var planeData = this.planeObjectsList.find(
        (plane) => plane.name == this.selectedCircleKey
      );
      var plainPoints = planeData.points;
      let removedPlanePoint = null;
      let removedGroupPoint = null;

      removedPlanePoint = plainPoints.plane_points.splice(indexToDelete, 1)[0];
      removedGroupPoint = this.groups.circles[this.selectedCircleKey].splice(
        indexToDelete,
        1
      )[0];
      planeData.points = JSON.stringify(plainPoints);
      this.deleteShow = false;
      const isDeleted = await this.addPlaneObject(planeData);
      planeData.points = plainPoints;
      if (!isDeleted) {
        planeData.points.plane_points.splice(
          indexToDelete,
          0,
          removedPlanePoint
        );
        this.groups.circles[this.selectedCircleKey].splice(
          indexToDelete,
          0,
          removedGroupPoint
        );
        this.toast.error('Failed to delete plane point.');
      } else {
        // sorting it according to ids
        let size = this.groups.circles[this.selectedCircleKey].length;
        for (let i = 0; i < size; i++) {
          let name =
            this.groups.circles[this.selectedCircleKey][i]['name'].split(
              '-'
            )[0];
          this.groups.circles[this.selectedCircleKey][i]['name'] =
            name + '-' + i;
        }
      }
    },

    async handleLayerMouseUp(e) {
      if (!Object.keys(this.selectedObject).length) return;
      let pos = this.$refs.layer.getNode().getRelativePointerPosition();

      const container = this.$refs.stage;
      const inGrey = this.checkPointInsideGreyRegion(
        container.oldProps.width,
        container.oldProps.height,
        pos.x,
        pos.y
      );

      const overlapRegion = this.checkIfpointOverlapEachOther(
        container.oldProps.width,
        container.oldProps.height,
        pos.x,
        pos.y
      );

      if (inGrey || overlapRegion)
        return this.toast.info(
          'Points cannot be placed in this location. Please try a different area.'
        );
      await this.addPointsInGroup(pos);
    },

    async handlePressEnter(e, object) {
      if (e.keyCode === 13) await this.handleChangePlaneColor(object);
    },
  },
};
</script>

<style>
.annotation-image-card > .ant-card-cover {
  padding: 1em !important;
  flex-grow: 1;
  display: flex !important;
}

.annotation-image-card > .ant-card-cover > div {
  display: flex !important;
}

.annotation-image-card > .ant-card-cover > div > .konvajs-content {
  width: unset !important;
  height: unset !important;
  flex-grow: 1 !important;
  display: flex;
}

.annotation-image-card > .ant-card-cover > div > .konvajs-content > canvas {
  border: 1.5px solid #d9d9d9 !important;
  border-radius: 5px;
  flex-grow: 1;
  width: 100% !important;
  height: 100% !important;
}

.static-objects-list {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.static-objects-list > .ant-list-header {
  background: lightgray !important;
  font-weight: 600 !important;
}

.static-objects-list > .ant-spin-nested-loading {
  flex-grow: 1;
  height: 1px;
  overflow-y: auto;
}

.greenCheck {
  padding: 10px;
  background-color: green;
  display: inline-block;
}

.color-box {
  width: 20px;
  height: 10px;
  display: inline-block;
  background: red;
}

.greenDot {
  height: 10px;
  width: 10px;
  background-color: green;
  border-radius: 50%;
  display: inline-block;
}

.disable-click {
  pointer-events: none;
  color: lightgray;
}
</style>
